import type { Messages } from './fr';

const it: Messages = {
  app: { tagline: 'Il gioco di lettere che parla più lingue.' },
  nav: {
    back: 'Indietro',
    home: 'Home',
    rules: 'Regole',
    settings: 'Impostazioni',
    about: 'Informazioni',
    account: 'Account',
    leaderboards: 'Classifiche',
    tournaments: 'Tornei',
    daily: 'Sfida del giorno',
    admin: 'Amministrazione',
  },
  common: {
    cancel: 'Annulla',
    confirm: 'Conferma',
    close: 'Chiudi',
    save: 'Salva',
    loading: 'Caricamento…',
    yes: 'Sì',
    no: 'No',
    copy: 'Copia',
    copied: 'Copiato!',
    retry: 'Riprova',
  },
  langNames: {
    fr: 'francese',
    en: 'inglese',
    es: 'spagnolo',
    de: 'tedesco',
    it: 'italiano',
    pt: 'portoghese',
  },
  home: {
    quickPlay: 'Gioca',
    quickPlayDesc: 'Partita Classica di 2 minuti · {{lang}}',
    modesTitle: 'Modalità di gioco',
    newGame: 'Nuova partita',
    online: 'Gioca online',
  },
  modes: {
    classic: {
      name: 'Classica',
      short: 'Una lingua · 2 min',
      desc: 'Una sola lingua, uguale per tutti. Fai più punti possibile in 120 secondi. Nessun bonus di lingua né per finire prima.',
    },
    polyglot: {
      name: 'Poliglotta comune',
      short: '2 o 3 lingue per tutti · 2 min',
      desc: 'Le stesse 2 o 3 lingue per tutti. Bonus diversità (+20 % con due lingue, +30 % con tre) se trovi almeno 3 parole in ogni lingua.',
    },
    duel: {
      name: 'Duello tattico',
      short: '10 parole o 2 min · contratto',
      desc: 'Ognuno sceglie in segreto 1, 2 o 3 lingue. Obiettivo: 10 parole valide. Contratto linguistico + bonus velocità se arrivi a 10 parole prima della fine. La 10ª parola chiude il tuo turno.',
    },
    sprint: {
      name: 'Sprint libero',
      short: 'Da 1 a 3 lingue · 2 min · contratto',
      desc: 'Scegli da 1 a 3 lingue e fai più punti possibile in 120 secondi con un contratto linguistico. Nessun bonus per finire prima.',
    },
    relax: {
      name: 'Relax',
      short: 'Senza cronometro',
      desc: 'Senza limite di tempo e senza classifica. Gli aiuti di validità si possono attivare solo in questa modalità.',
    },
  },
  setup: {
    title: 'Nuova partita',
    mode: 'Modalità',
    languagesShared: 'Lingue della partita (da {{min}} a {{max}})',
    languagesIndividual: 'Le tue lingue (da 1 a 3, bloccate prima dell’estrazione)',
    errors: {
      duplicate_language: 'Ogni lingua può essere scelta una sola volta.',
      too_few_languages: 'Scegli almeno {{min}} lingua/e.',
      too_many_languages: 'Al massimo {{max}} lingua/e.',
    },
    noLanguageBonus: 'Nessun bonus di lingua in questa modalità.',
    opponent: 'Avversario',
    noOpponent: 'Nessuno (solo)',
    robotNotice:
      'I robot sono programmi di allenamento chiaramente identificati. Non contano mai nelle classifiche.',
    robotSameLanguages: 'Il robot gioca con le mie lingue',
    robotLanguages: 'Lingue del robot',
    loading: 'Preparazione dell’estrazione…',
    start: 'Inizia',
    noDraw:
      'Nessuna estrazione equilibrata trovata per questa combinazione. Prova altre lingue o il contratto Specialista.',
  },
  contracts: {
    specialist: { name: 'Specialista', desc: 'una lingua, bonus garantito sui punti base.' },
    duo: { name: 'Duo', desc: 'due lingue, bonus solo con almeno 3 parole distinte in ciascuna.' },
    trio: {
      name: 'Trio',
      desc: 'tre lingue, bonus solo con almeno 3 parole distinte in ciascuna.',
    },
    diversity: {
      name: 'Diversità',
      desc: 'bonus del {{pct}} % per tutti con almeno 3 parole per lingua.',
    },
    short: { specialist: 'Specialista', duo: 'Duo', trio: 'Trio', diversity: 'Diversità' },
  },
  bots: {
    rookie: 'Principiante',
    casual: 'Intermedio',
    skilled: 'Esperto',
    expert: 'Campione',
    rookieDesc: 'Parole molto comuni, ritmo lento, preferisce parole corte.',
    casualDesc: 'Parole comuni, ritmo moderato.',
    skilledDesc: 'Vocabolario comune ampio, ritmo sostenuto.',
    expertDesc: 'Conosce anche parole rare, veloce, punta alle parole lunghe.',
    robotName: 'Robot {{level}}',
  },
  game: {
    timeLeft: 'Tempo rimasto',
    getReady: 'Pronti…',
    untimed: '∞',
    score: 'Punteggio',
    points: 'pt',
    wordsOfTarget: '{{n}} / {{target}} parole',
    wordsCount_one: '{{count}} parola',
    wordsCount_other: '{{count}} parole',
    languageCounters: 'Parole per lingua',
    robotNotice: 'Avversario robot (programma di allenamento)',
    opponentDone: 'finito',
    finalWarning:
      'Attenzione: la tua prossima parola valida sarà la 10ª e chiuderà il turno. Il tuo contratto non è ancora rispettato.',
    currentWord: 'Parola in corso',
    tapLetters: 'Tocca le lettere',
    hintValid: 'Valida: {{langs}}',
    hintInvalid: 'Non (ancora) una parola',
    attributeTo: 'Assegna a {{lang}}',
    attribution: 'Lingua:',
    auto: 'Auto',
    autoExplain:
      'Auto: la parola va alla lingua valida che ha più bisogno di parole per il tuo contratto, altrimenti secondo l’ordine delle tue lingue.',
    rack: 'Lettere dell’estrazione',
    tileLabel: 'Lettera {{letter}}, posizione {{pos}}',
    shuffle: 'Mescola',
    delete: 'Cancella',
    clear: 'Svuota',
    submit: 'Conferma',
    foundWords: 'Parole trovate',
    bonusSecured: 'bonus di lingua ottenuto',
    finish: 'Termina',
    stop: 'Fermati',
    stopTitle: 'Fermare il turno?',
    stopBody: 'Le tue parole restano. Fermarsi prima non dà alcun bonus.',
    finishBody: 'Terminare la partita relax e vedere il riepilogo?',
    confirmFinalTitle: 'Ultima parola: contratto non rispettato',
    confirmFinalOk: 'Conferma comunque',
    confirmFinalBody:
      'Questa 10ª parola chiude subito il tuo turno. Il contratto linguistico non sarà rispettato: niente bonus di lingua (il bonus velocità resta).',
    accepted: '«{{word}}» +{{points}} ({{lang}})',
  },
  reasons: {
    round_over: 'Il turno è finito.',
    invalid_characters: 'Caratteri non giocabili.',
    too_short: 'Minimo tre lettere.',
    too_long: 'Parola troppo lunga.',
    not_formable: 'Impossibile con queste lettere.',
    duplicate: 'Già trovata.',
    language_not_allowed: 'Lingua non ammessa in questo turno.',
    excluded_word: 'Parola esclusa dalla politica sui contenuti del gioco.',
    not_a_word: 'Parola sconosciuta nelle tue lingue.',
    wrong_language: 'Questa parola è valida in: {{langs}}.',
    confirmation_required: 'Serve una conferma.',
    rate_limited: 'Troppi tentativi, rallenta.',
    not_started: 'Il turno non è ancora iniziato.',
  },
  results: {
    points: 'punti',
    pts: 'pt',
    base: 'Punti base',
    fulfilled: 'rispettato',
    missed: 'mancato',
    speedBonus: 'Bonus velocità',
    officialTime: 'Tempo ufficiale',
    target: 'Obiettivo',
    endReason: {
      target_reached: 'Obiettivo raggiunto',
      time_up: 'Tempo scaduto',
      stopped: 'Fermato',
      abandoned: 'Abbandono',
    },
    explainBase: 'Le tue parole valgono {{base}} punti base (10 × (lettere − 2)).',
    explainSpecialist: 'Contratto Specialista: +10 % ottenuto.',
    explainQuotaOk: 'Almeno {{quota}} parole in ogni lingua: bonus del {{pct}} % ottenuto.',
    explainQuotaKo:
      'Servivano almeno {{quota}} parole in ogni lingua: niente bonus di lingua ({{pct}} % possibile).',
    explainSpeedOk: '10 parole raggiunte in {{time}}: bonus velocità del {{pct}} %.',
    explainSpeedKo: 'Obiettivo di 10 parole non raggiunto in tempo: niente bonus velocità.',
    explainFormula:
      'Totale = base × (1 + bonus di lingua + bonus velocità), arrotondato una sola volta al centesimo.',
    win: 'Vittoria!',
    lose: 'Sconfitta',
    tie: 'Pareggio',
    tieExplain: 'Punteggi esattamente identici: l’incontro è dichiarato pari.',
    you: 'Tu',
    robotTitle: '{{name}} (robot)',
    missedWords: 'Parole comuni che potevi formare',
    missedWordsNote:
      'Elenco indicativo di parole frequenti nelle tue lingue, per la prossima volta.',
    shareText: '{{mode}} ({{langs}}): {{score}} punti, {{words}} parole. Chi fa meglio?',
    playAgain: 'Gioca ancora',
    share: 'Condividi il risultato',
    changeSetup: 'Cambia modalità',
    localNotice: 'Partita locale: non entra in nessuna classifica.',
    savedToHistory: 'Salvata in «Le mie partite» (allenamento).',
    drawAudit: 'Estrazione {{seed}} ({{gen}})',
  },
  errors: {
    network: 'Connessione persa. Nuovo tentativo…',
    lexiconLoad: 'Impossibile caricare il dizionario. Controlla la connessione e riprova.',
    notFound: 'Pagina non trovata',
    generic: 'Si è verificato un errore.',
  },
  online: {
    title: 'Online',
    challengeFriend: 'Sfida un amico',
    challengeFriendDesc: 'Link di invito, ognuno gioca quando vuole',
    liveDuel: 'Duello dal vivo',
    liveDuelDesc: 'Stanza, invito, partita simultanea',
    myGames: 'Le mie partite',
    offlineNote:
      'Server irraggiungibile: sono disponibili solo il gioco in solitario e l’allenamento.',
    guestBanner:
      'Stai giocando come ospite. Crea un account per salvare lo storico ed entrare nelle classifiche.',
    saveAccount: 'Salva il mio account',
  },
  challenge: {
    newTitle: 'Sfida un amico',
    returnMatch: 'Andata e ritorno (2 turni, estrazioni incrociate, senza classifica)',
    create: 'Crea la sfida',
    created: 'Sfida creata! Invia questo link:',
    share: 'Condividi il link',
    shareText: 'Ti sfido ({{mode}})!',
    expires: 'Scade il {{date}}',
    playMine: 'Gioca ora il mio turno',
    inviteFrom: '{{name}} ti sfida!',
    acceptTitle: 'Invito',
    chooseLanguages: 'Scegli le tue lingue (bloccate dopo la conferma)',
    compatibleOnly:
      'Sono proposte solo le combinazioni compatibili con l’estrazione di questa sfida.',
    accept: 'Accetta la sfida',
    expired: 'Questa sfida è scaduta.',
    taken: 'Questa sfida è già stata accettata da qualcun altro.',
    own: 'È la tua sfida: condividi il link con un amico.',
    notFound: 'Sfida non trovata.',
    returnInfo:
      'Andata e ritorno: due turni su due estrazioni diverse; nessun risultato viene rivelato prima della fine.',
    goToMatch: 'Vai alla partita',
  },
  match: {
    readyTitle: 'Il tuo turno',
    readyBody:
      'Il cronometro parte appena compaiono le lettere. Chiudere o ricaricare la pagina non azzera il tempo.',
    start: 'Inizia il mio turno',
    waitingOpponent:
      'In attesa dell’avversario. Le sue parole e il suo punteggio restano nascosti fino alla fine.',
    opponentProgress: '{{name}}: {{count}} parola/e',
    leg: 'Turno {{n}} / 2',
    nextLeg: 'Gioca il turno successivo',
    encounterTotal: 'Totale dell’incontro',
    unranked: 'Partita non classificata',
    ranked: 'Partita classificata',
    reportWord: 'Segnala una parola rifiutata',
    reportPrompt: 'Parola da segnalare (e lingua)',
    reportSent: 'Grazie, segnalazione inviata.',
    loadError: 'Impossibile caricare la partita.',
    opponentNotPlayed: 'L’avversario non ha giocato.',
    opponent: 'Avversario',
  },
  account: {
    title: 'Il mio account',
    guest: 'Account ospite',
    registered: 'Account salvato',
    register: 'Crea un account',
    login: 'Accedi',
    logout: 'Esci',
    username: 'Nome utente (3–20 caratteri)',
    password: 'Password (min. 10 caratteri)',
    email: 'E-mail (facoltativa)',
    displayName: 'Nome visualizzato',
    saveHint: 'Lo storico da ospite viene conservato quando crei l’account.',
    history: 'Storico',
    export: 'Esporta i miei dati',
    delete: 'Elimina il mio account',
    deleteConfirm: 'Eliminare definitivamente l’account? I tuoi dati personali saranno cancellati.',
    notifications: 'Promemoria',
    remindersOptIn: 'Ricevi promemoria (al massimo uno al giorno, mai nelle ore di silenzio)',
    quietHours: 'Ore di silenzio (dalle … alle …)',
    haveAccount: 'Hai già un account?',
    noAccount: 'Non hai ancora un account?',
    saved: 'Salvato.',
    errors: {
      invalid_credentials: 'Nome utente o password errati.',
      username_or_email_taken: 'Nome utente o e-mail già in uso.',
      nickname_refused: 'Questo nome non è consentito.',
      nickname_reserved: 'Questo nome è riservato.',
      nickname_length: 'Tra 3 e 20 caratteri.',
      nickname_characters: 'Caratteri non consentiti.',
      invalid_request: 'Controlla i campi.',
      account_suspended: 'Account sospeso.',
      generic: 'Si è verificato un errore.',
    },
  },
  live: {
    searching: 'Ricerca di un avversario…',
    searchingBody: 'Attesa: {{seconds}} s',
    noFakePlayers:
      'Qui vengono proposti solo giocatori reali; nessun robot viene presentato come umano.',
    fallbackTitle: 'Ancora nessuno. Puoi:',
    fallbackRobot: 'Giocare contro un robot (allenamento, senza classifica)',
    fallbackAsync: 'Creare una sfida asincrona da condividere',
    othersSearching_zero: 'Nessun altro giocatore cerca questo tipo di partita in questo momento.',
    othersSearching_one:
      '{{count}} altro giocatore cerca questo tipo di partita in questo momento.',
    othersSearching_other:
      '{{count}} altri giocatori cercano questo tipo di partita in questo momento.',
    noOpponentFound: 'Nessuno è disponibile per ora. Puoi:',
    searchAgain: 'Cerca di nuovo',
    languagesSecret: 'Le tue lingue (segrete, bloccate prima dell’estrazione)',
    quick: 'Partita rapida',
    createRoom: 'Crea una stanza e invita',
    rankedNote:
      'Le partite tra account salvati contano per la classifica (ufficiale o sperimentale secondo la modalità).',
    roomFull: 'Questa stanza è piena.',
    roomNotFound: 'Stanza non trovata o scaduta.',
    offline: 'disconnesso',
    online: 'connesso',
    opponentReady: 'pronto',
    waitingOpponent: 'In attesa di un avversario…',
    shareRoom: 'Invita un amico con questo link:',
    sharedReady: 'Lingue fissate per entrambi i giocatori.',
    lock: 'Sono pronto',
    waitingLock: 'In attesa della scelta dell’avversario…',
    noDrawForBoth:
      'Nessuna estrazione equilibrata per entrambe le configurazioni. Scegli altre lingue.',
    disconnectRule:
      'Regola di disconnessione: l’orologio del server continua; torna su questa pagina per riprendere il tuo turno da dove si trova.',
    opponentLeft:
      '{{name}} si è disconnesso. Il suo cronometro continua; può tornare prima della fine del round.',
    opponentBack: '{{name}} è tornato.',
    reconnecting:
      'Connessione interrotta, riconnessione in corso… Le parole già convalidate restano.',
    rematch: 'Rivincita',
    joinRematch: 'Unisciti alla rivincita',
    reactionsTitle: 'Reazioni',
    reactions: {
      gg: 'Ben giocato!',
      bravo: 'Bravo!',
      wow: 'Impressionante!',
      oops: 'Ops!',
      thanks: 'Grazie!',
      good_luck: 'Buona fortuna!',
    },
  },
  boards: {
    title: 'Classifiche',
    official: 'Ufficiale',
    experimental: 'Sperimentale',
    officialDesc: 'Modalità simmetriche (Classica, Poliglotta comune): stesse lingue per tutti.',
    experimentalDesc:
      'Duello tattico e Sprint libero: classifica separata, non confrontabile con le modalità simmetriche.',
    myRank: 'La tua posizione: {{rank}} · {{rating}} Elo · {{games}} partite',
    empty: 'Ancora nessun risultato.',
    player: 'Giocatore',
    rating: 'Elo',
    games: 'Partite',
    rules:
      'Contano solo le partite tra account salvati. Robot, partite locali e incontri di andata e ritorno non entrano mai in classifica.',
  },
  tournaments: {
    title: 'Tornei',
    intro:
      'Tornei settimanali asincroni: più turni, un tentativo per turno, stesse lettere per tutti.',
    none: 'Nessun torneo al momento.',
    weeklyTitle: 'Settimanale {{mode}} {{langs}} · {{period}}',
    rounds: '{{n}} turno/i',
    entrants: '{{n}} iscritto/i',
    registered: 'iscritto',
    status: {
      scheduled: 'In arrivo',
      open: 'In corso',
      closing: 'Chiusura',
      closed: 'Concluso',
      cancelled: 'Annullato',
    },
    frozen: 'Regole {{rules}} e dizionari {{lexicons}} fissati per tutto il torneo.',
    attempts: '{{n}} tentativo/i per turno.',
    sharingLimit:
      'Limite noto: in una competizione asincrona pubblica i giocatori possono scambiarsi le risposte. Il gioco non pretende di impedirlo del tutto.',
    register: 'Iscrivimi',
    accountRequired: 'Per i tornei serve un account salvato.',
    myRounds: 'I miei turni',
    round: 'Turno {{n}}',
    resume: 'Riprendi',
    play: 'Gioca',
    standings: 'Classifica del torneo',
    roundsPlayed: '{{n}} turno/i',
    tieRule: 'Parità esatta: stessa posizione, nessuno spareggio nascosto.',
    backToTournament: 'Torna al torneo',
    finalStandings: 'Classifica finale',
    myResult: 'Il tuo totale: {{total}} punti · posizione {{rank}} su {{n}}',
    noRoundYet: 'Nessun round giocato per ora.',
    startsAt: 'Inizio: {{date}}',
    abandoned: 'Non terminato',
    retry: 'Nuovo tentativo ({{n}} rimasti)',
    closingNote:
      'Chiusura in corso: i round già iniziati possono finire; la classifica finale arriva tra pochi minuti.',
    errors: {
      competition_not_found: 'Torneo non trovato.',
      invitation_required: 'Questo torneo è privato: serve un invito.',
      attempts_exhausted: 'Tentativo già usato per questo turno.',
      competition_closed: 'Il torneo è concluso.',
      competition_not_started: 'Il torneo non è iniziato.',
      not_registered: 'Iscriviti prima.',
      account_required: 'Serve un account salvato.',
    },
  },
  daily: {
    title: 'Sfida del giorno',
    intro: 'Un’estrazione per categoria e per giorno (UTC), uguale per tutti. Un solo tentativo.',
    back: 'Torna alle sfide del giorno',
    play: 'Gioca',
    inProgress: 'In corso',
    resets: 'Nuova sfida il {{time}}.',
  },
  profile: {
    title: 'Il mio profilo',
    streak: 'giorni di serie',
    bestStreak: 'serie migliore',
    rounds: 'partite online',
    distinctWords: 'parole diverse',
    streakRules:
      'La serie guadagna un giorno quando concludi una partita online con almeno una parola valida (sfida del giorno, sfida tra amici, duello dal vivo o torneo), al massimo una volta al giorno. Giorni di grazia gratuiti rimasti: {{grace}} (rinnovati ogni 30 giorni). Fuso: {{tz}}.',
    useMyTimezone: 'Usa il mio fuso ({{tz}})',
    tzTooSoon: 'Il fuso orario si può cambiare una volta ogni 7 giorni.',
    badges: 'Distintivi',
    noBadges: 'Ancora nessun distintivo.',
    badgesNote: 'I distintivi si basano solo su eventi verificati dal server.',
    cosmetics: 'Personalizzazione',
    cosmeticsNote: 'Puramente visiva: nessun effetto su punteggio o estrazioni.',
    plusOnly: 'Solo Plus',
    earnIt: 'Si sblocca giocando',
    slot: {
      theme: 'Tema',
      avatar: 'Avatar',
      frame: 'Cornice',
    },
    history: 'Le mie partite',
    gamesSummary: '{{online}} online · {{practice}} allenamento/i',
    noGames: 'Ancora nessuna partita salvata.',
    pendingSync: '{{n}} allenamento/i in attesa di invio (offline).',
    gamesNote:
      'Gli allenamenti (solo, robot, relax) si giocano sul tuo dispositivo e vengono salvati qui. Il server verifica le parole e ricalcola il punteggio, ma non può verificare il tempo: non contano per la serie, i badge o le classifiche.',
    sourceOnline: 'Online',
    sourcePractice: 'Allenamento',
    waitingOpponent: 'risultato dopo l’avversario',
    playDaily: 'Gioca la sfida del giorno',
  },
  badges: {
    first_round: 'Primo turno',
    nine_letters: 'Parola di 9 lettere',
    trio_contract: 'Contratto Trio rispettato',
    speed_60: '10 parole in meno di 60 s',
    words_100: '100 parole trovate',
    words_1000: '1000 parole trovate',
    streak_7: 'Serie di 7 giorni',
    streak_30: 'Serie di 30 giorni',
    first_duel_win: 'Prima vittoria in duello',
    tournament_participant: 'Partecipante a un torneo',
    tournament_winner: 'Vincitore di torneo',
    tournament_podium: 'Podio di torneo',
    tournament_top10: 'Top 10 di torneo',
    daily_participant: 'Sfida del giorno',
    team_weekly: 'Obiettivo di squadra raggiunto',
  },
  cosmetics: {
    theme_default: 'Classico',
    theme_ocean: 'Oceano',
    theme_aurora: 'Aurora',
    theme_sakura: 'Sakura',
    theme_mono: 'Monocromatico',
    avatar_letter: 'Iniziale',
    avatar_owl: 'Gufo',
    avatar_fox: 'Volpe',
    frame_none: 'Nessuna',
    frame_team: 'Squadra',
    frame_champion: 'Campione',
  },
  reminders: {
    title: 'Promemoria',
    explain:
      'Facoltativi: al massimo uno al giorno, mai nelle tue ore di silenzio e nessuno se hai già giocato. Disattivabili in ogni momento.',
    unavailable: 'I promemoria non sono disponibili su questo server o dispositivo.',
    quietStart: 'Ore di silenzio: inizio',
    quietEnd: 'Ore di silenzio: fine',
    enable: 'Attiva i promemoria',
    disable: 'Disattiva',
    enabled: 'Promemoria attivati.',
    disabled: 'Promemoria disattivati.',
    denied: 'Permesso negato dal browser.',
  },
  journal: {
    title: 'Quaderno delle parole',
    intro: 'Le parole che hai trovato online, per lingua.',
    all: 'Tutte',
    search: 'Cerca una parola',
    empty: 'Ancora nessuna parola.',
  },
  friends: {
    title: 'Amici',
    inbox: 'Sfide ricevute',
    inboxNote: 'Apri il link che ti ha inviato il tuo amico per giocare.',
    invite: 'Invita un amico',
    inviteNote: 'Link valido 7 giorni. Nessuna chat libera: solo sfide e reazioni predefinite.',
    none: 'Ancora nessun amico.',
    challenge: 'Sfida',
    remove: 'Rimuovi',
    ownInvite: 'È il tuo invito.',
    inviteInvalid: 'Invito non valido o scaduto.',
  },
  team: {
    title: 'Squadra',
    intro:
      'Unisciti o crea una squadra (max. 20 membri) e raggiungete insieme un obiettivo settimanale di parole. Ricompense solo estetiche.',
    name: 'Nome della squadra',
    create: 'Crea la squadra',
    weekly: 'Obiettivo della settimana {{week}}',
    progress: '{{words}} / {{goal}} parole',
    rewardNote: 'Obiettivo raggiunto: cornice «Squadra» per ogni membro.',
    members: 'Membri ({{n}})',
    leave: 'Lascia la squadra',
    accountRequired: 'Serve un account salvato.',
    joinFailed: 'Impossibile unirsi a questa squadra.',
  },
  moderation: {
    block: 'Blocca questo giocatore',
    blocked: 'Giocatore bloccato.',
    report: 'Segnala…',
    reported: 'Segnalazione inviata.',
    reasons: {
      nickname: 'Nome inappropriato',
      cheating: 'Sospetto imbroglio',
      harassment: 'Molestie',
      other: 'Altro',
    },
  },
  plus: {
    intro: 'Plus sostiene il gioco e aggiunge strumenti, senza mai toccare il punteggio:',
    fairness:
      'Il cuore del gioco, le sei lingue, l’allenamento, le sfide e la competizione pubblica restano gratuiti. Nessuna impostazione di difficoltà dipende dall’abbonamento.',
    features: {
      advanced_stats: 'Statistiche avanzate',
      challenge_archives: 'Archivio completo delle sfide concluse',
      personal_training: 'Allenamento personalizzato deterministico',
      customisation: 'Temi e avatar esclusivi',
      private_tournaments: 'Strumenti per tornei privati',
    },
    monthly: 'Abbonati — {{price}}',
    yearly: 'Piano annuale — {{price}}',
    unavailable: 'Plus non è ancora acquistabile.',
    testMode: 'Modalità test: nessun pagamento reale.',
    confirming: 'Pagamento in conferma… (attivato solo alla conferma del fornitore)',
    cancelled: 'Pagamento annullato.',
    active: 'Plus attivo',
    until: 'fino al {{date}}',
    manage: 'Gestisci l’abbonamento',
    stats: 'Statistiche',
    mode: 'Modalità',
    rounds: 'Turni',
    avgScore: 'Punteggio medio',
    contractRate: 'Contratti rispettati',
    secPerWord: 's / parola',
    train: 'Allenamento personalizzato ({{lang}})',
    archive: 'Archivio delle sfide concluse',
    archiveEmpty: 'Ancora nessuna partita conclusa.',
    archiveMore: 'Mostra altro',
    archiveBot: 'robot',
    pricesNote:
      'Prezzi indicativi soggetti a modifiche; l’importo esatto è mostrato nella pagina di pagamento.',
    notOffered: 'In questa versione non è proposto alcun abbonamento: tutto il gioco è gratuito.',
  },
  legal: {
    draftBanner: 'Bozza — da completare con l’identità reale dell’editore.',
    privacy: 'Privacy',
    terms: 'Condizioni',
    notice: 'Note legali',
  },
  offline: {
    title: 'Giocare offline',
    explain:
      'Scarica i dizionari per giocare in solitario e allenarti senza connessione. Le partite offline non entrano in nessuna classifica.',
    download: 'Scarica',
    ready: 'Disponibile',
  },
  play: { noGame: 'Nessuna partita in corso.' },
  install: {
    body: 'Installa il gioco nella schermata iniziale per giocare più in fretta, anche offline in solitario.',
    cta: 'Installa',
    later: 'Più tardi',
  },
  pwa: {
    updateReady: 'È pronta una nuova versione.',
    reload: 'Aggiorna',
    offline:
      'Offline: il gioco in solitario e l’allenamento restano disponibili con i dizionari già scaricati.',
  },
  settings: {
    title: 'Impostazioni',
    uiLanguage: 'Lingua dell’interfaccia',
    uiLanguageNote:
      'La lingua dell’interfaccia è indipendente dalle lingue ammesse nelle tue partite.',
    sound: 'Suoni',
    haptics: 'Vibrazione (se supportata)',
    motion: 'Animazioni',
    motionSystem: 'Come il sistema',
    motionReduced: 'Ridotte',
    motionFull: 'Normali',
    relaxHints: 'Aiuti di validità in modalità Relax',
  },
  rules: {
    title: 'Regole del gioco',
    goal: {
      title: 'Obiettivo',
      body: [
        'Forma parole con le 9 lettere dell’estrazione toccandole nell’ordine che vuoi, senza vincoli di vicinanza.',
        'Ogni tessera si usa una sola volta per parola; tutte tornano disponibili per la parola successiva. Mescolare non cambia l’estrazione.',
        'Minimo tre lettere. Una stessa parola conta una sola volta per turno.',
      ],
    },
    tiles: {
      title: 'Lettere e accenti',
      body: [
        'Gli accenti non hanno una tessera propria: «città» si gioca C-I-T-T-A e si mostra con l’accento.',
        'Ñ (spagnolo) e Ä, Ö, Ü (tedesco) sono tessere distinte, utilizzabili solo nella loro lingua. ß si scrive con due S; œ ed æ usano due tessere.',
      ],
    },
    languages: {
      title: 'Lingue e assegnazione',
      body: [
        'Una parola valida in più delle tue lingue viene assegnata a una sola. Scegli la lingua prima di confermare (pulsanti Lingua) oppure lascia «Auto».',
        'Auto sceglie la lingua valida che ha più bisogno di parole per il tuo contratto, poi l’ordine delle tue lingue. Una volta accettata, la parola non cambia più lingua.',
        'Nomi propri e abbreviazioni sono esclusi. Alcune parole offensive sono escluse dalla politica sui contenuti. Potrai segnalare una parola rifiutata.',
      ],
    },
    points: {
      title: 'Punti',
      body: [
        'Una parola di n lettere (tessere) vale 10 × (n − 2): 3 lettere = 10, 4 = 20 … 9 = 70.',
        'Punteggio finale = punti base × (1 + bonus di lingua + bonus velocità). I bonus si sommano e il totale si arrotonda una sola volta al centesimo.',
        'Una parola rifiutata non vale nulla e non conta per l’obiettivo.',
      ],
    },
    contracts: {
      title: 'Contratti (Duello tattico, Sprint libero)',
      body: [
        'Specialista (1 lingua): +10 %. Duo (2 lingue): +20 % con almeno 3 parole in ciascuna. Trio (3 lingue): +30 % con almeno 3 parole in ciascuna.',
        'Contratto mancato: conservi i punti base, senza bonus di lingua e senza ripiego su un contratto inferiore.',
        'Poliglotta comune: +20 % (2 lingue) o +30 % (3 lingue) con almeno 3 parole per lingua, uguale per tutti.',
      ],
    },
    speed: {
      title: 'Bonus velocità (Duello tattico)',
      body: [
        'Se raggiungi 10 parole valide prima di 120 s: bonus = 20 % × (120 − t) / 120, dove t è il tempo ufficiale misurato dal server. A 90 s: +5 %; a 60 s: +10 %; a 30 s: +15 %.',
        'La 10ª parola accettata chiude subito il tuo turno. Il gioco ti avvisa se il contratto non è rispettato.',
      ],
    },
    modes: {
      title: 'Modalità',
      body: [
        'Classica, Poliglotta comune, Duello tattico, Sprint libero, Relax (senza cronometro né classifica) e allenamento contro i robot.',
        'Parità esatta: l’incontro è dichiarato pari, senza spareggi nascosti sulla velocità.',
      ],
    },
    fairness: {
      title: 'Equità',
      body: [
        'La stessa estrazione non ha la stessa difficoltà in tutte le lingue. Ogni estrazione viene verificata per offrire abbastanza parole comuni a ogni configurazione, senza garantire un’equità perfetta.',
        'I parametri sono ipotesi in fase di test; una partita conserva sempre la versione di regole e dizionari con cui è iniziata.',
      ],
    },
    robots: {
      title: 'Robot',
      body: [
        'I robot sono programmi deterministici (nessuna IA generativa) identificati come tali. Giocano solo parole valide e non entrano mai nelle classifiche umane.',
      ],
    },
    keyboard: {
      title: 'Tastiera',
      body: 'Digita le lettere (o 1–9 per la posizione di una tessera), Invio per confermare, Backspace per cancellare, Esc per svuotare, Spazio per mescolare.',
    },
  },
  about: {
    title: 'Informazioni',
    disclaimer:
      'Un gioco di lettere per il piacere di giocare in più lingue. Non ha un’efficacia didattica dimostrata.',
    dictionaries: 'Dizionari e dati',
    frequencies: 'Frequenze delle parole',
    policy: 'Politica sui contenuti',
    adapted: 'elenco adattato',
    noticeFiles:
      'Le note complete (fonti, versioni, licenze) accompagnano ogni dizionario scaricato.',
    noDefinitions:
      'Nessuna definizione o traduzione viene mostrata finché non è integrata una fonte con licenza compatibile.',
  },
};

export default it;
